/**
 * Which thinkers this person mentions, and how recently.
 *
 * **Per viewer, per device, and there is no endpoint for it** — the registry read returns the
 * whole list with no query parameter, so all filtering and ranking are client-side by design.
 * Two people in the same workspace mention different thinkers, and neither ordering is the
 * workspace's business.
 *
 * Every read and write swallows its errors: a private window with storage blocked must still
 * be able to mention somebody, just without the ordering.
 */

const KEY = "mention.thinker.recency";

type Recency = Record<string, number>;

export function readThinkerRecency(): Recency {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Recency)
      : {};
  } catch {
    return {};
  }
}

export function bumpThinkerRecency(thinkerId: string, now = Date.now()): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...readThinkerRecency(), [thinkerId]: now }));
  } catch {
    // Storage is unavailable; the mention still works, it just will not rank.
  }
}

/**
 * Name substring first, then most-recently-mentioned. `limit` is 25 while searching and 5 on
 * an empty query — a long list is useful when somebody is looking for a name and noise when
 * they have only typed `@`.
 */
export function rankThinkers<T extends { id: string; name: string }>(
  thinkers: T[],
  query: string,
  recency: Recency = readThinkerRecency(),
): T[] {
  const needle = query.trim().toLowerCase();
  const matched = needle
    ? thinkers.filter((thinker) => thinker.name.toLowerCase().includes(needle))
    : [...thinkers];
  matched.sort((a, b) => (recency[b.id] ?? 0) - (recency[a.id] ?? 0));
  return matched.slice(0, needle ? 25 : 5);
}

/**
 * Section order in the mention menu. Thinkers rank ABOVE people and documents — the addition
 * this wave makes exists because a thinker is who you most often want when you type `@` in a
 * document that is being worked on.
 */
export const MENTION_SECTION_PRIORITY = {
  thinker: 1,
  user: 0.5,
  document: 0.25,
} as const;

export type MentionKind = keyof typeof MENTION_SECTION_PRIORITY;

export interface MentionCandidate {
  kind: MentionKind;
  id: string;
  label: string;
  /** The entity's own id — a thinker id, a user id, or a document id. */
  modelId: string;
}

/** The merged, ordered candidate list the menu renders. */
export function orderMentionCandidates(candidates: MentionCandidate[]): MentionCandidate[] {
  return [...candidates].sort(
    (a, b) => MENTION_SECTION_PRIORITY[b.kind] - MENTION_SECTION_PRIORITY[a.kind],
  );
}
